/**
 * Saved audience tag → personas-filter shape. One small pure function, no imports.
 *
 * One source for the web app (Home Lab segment cuts, focus-group launch) and the
 * backend (Slack Twins segment members, `FeaturedTwinsService.listSegmentMembers`),
 * so a segment draws the same twins in both. It moved here from
 * `apps/frontend/src/lib/audience-tag-filters.ts` (plan slack-twins E1b); there is
 * no parity copy.
 *
 * Counts drawn through it can drift from the stamped membership that
 * `matchAudienceTagNames` (backend `common/utils/match-audience-tags.ts`) produces;
 * that is accepted for parity with the web picker.
 */

// AudienceTag re-typed minimally here so this module stays React/import-light. The hook
// passes real `AudienceTag`s from @digital-twins/shared-types (structurally compatible).
export interface TagConditionLike {
  sourceColumn: string
  values: Array<string>
}
export interface TagLike {
  name: string
  conditions: Array<TagConditionLike>
}

// Map a saved tag's AND-combined conditions to the personas-filter shape the populations API
// expects (`Record<sourceColumn, values>` — keys AND together, values within a key OR). Two
// conditions on the SAME column merge their (de-duped) values. Blank columns/values are
// dropped so a malformed condition can never produce an empty-keyed filter. Pure + exported.
export function tagConditionsToFilters(tag: TagLike): Record<string, Array<string>> {
  const out: Record<string, Array<string>> = {}
  for (const cond of tag?.conditions ?? []) {
    const col = String(cond?.sourceColumn ?? '').trim()
    if (!col) continue
    const values = (cond?.values ?? []).map((v) => String(v)).filter((v) => v.trim() !== '')
    if (values.length === 0) continue
    const merged = new Set([...(out[col] ?? []), ...values])
    out[col] = [...merged]
  }
  return out
}
